import * as React from "react";
import { app, FrameContexts, pages } from "@microsoft/teams-js";
import { RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Dropdown, Flex, Loader, Text, RadioGroup, UserPhoneIcon, PollIcon } from '@fluentui/react-northstar';
import { getMeetingsData } from "../../api/publicapp-api";
import Channels from "../../constants/channels";
import "./config-tab.scss";
import { ImageUtil } from "../../helpers/avatar-helper";

type dropdownItem = {
    key: string,
    header?: string,
    content?: string,
}

enum EConfigTabs {
    panel = "panel",
    dashboard = "dashboard",
}

enum ETabEntity {
    panel = "atendimento",
    dashboard = "dashboard",
}

enum EPanelRoutes {
    unit = "painel-atendimento",
    team = "painel-atendimento-equipe",
}

enum EDashboardRoute {
    route = "dashboards"
}

enum EDashboardParams {
    general = "general",
    team = "team",
}

enum EDashboardTypes {
    general = "geral",
    team = "equipeDashboard",
}

export interface IConfigState {
    loader: boolean,
    loading: boolean,
    meetings: any[] | null,
    ancora: string | null,
    meetingTitle: string | null,
    selectedMeeting: any | null,
    noResultMessage: string,
    channelId?: string,
    channelName?: string,
    teamId?: string,
    teamGroupId?: string,
    selectedPanelType?: string | number,
    selectedDashboardType?: string | number,
    selectedAncora?: string | null,
    teamName?: string,
    frameContext: FrameContexts | string,
    selectedTab: EConfigTabs | null,
}

export type ConfigProps = {} & RouteComponentProps & WithTranslation;

class ConfigTab extends React.Component<ConfigProps, IConfigState> {
    readonly localize: TFunction;

    searchParams?: any = null;
    appId?: any = null;

    constructor(props: ConfigProps) {
        super(props);
        this.localize = this.props.t;
        this.state = {
            loader: true,
            loading: true,
            meetings: null,
            ancora: null,
            meetingTitle: null,
            selectedMeeting: null,
            noResultMessage: "",
            channelId: "",
            frameContext: "",
            selectedTab: null,
        }

        this.searchParams = new URLSearchParams(window.location.search);
        this.appId = this.searchParams.get("appId");
    }

    public componentDidMount() {
        app.getContext().then((context: app.Context) => {
            this.setState({
                channelId: context.channel.id,
                channelName: context.channel.displayName,
                teamId: context.team.internalId,
                teamGroupId: context.team.groupId,
                teamName: context.team.displayName,
                frameContext: context.page.frameContext,
            }, () => {
                this.init();
                pages.config.setValidityState(false);
            });
        });

        pages.getConfig().then((value: pages.InstanceConfig) => {
            if (value.contentUrl != "") {
                if (value.entityId === ETabEntity.panel) {
                    const contentUrl = new URL(value.contentUrl);
                    const params = new URLSearchParams(contentUrl.search);

                    this.setState({
                        selectedTab: EConfigTabs.panel,
                        selectedPanelType: value.contentUrl.includes(EPanelRoutes.team) ? "equipe" : "unidade",
                        selectedAncora: !value.contentUrl.includes(EPanelRoutes.team) ? params.get("ancora") : null,
                    });
                }

                if (value.entityId === ETabEntity.dashboard) {
                    this.setState({
                        selectedTab: EConfigTabs.dashboard,
                        selectedDashboardType: value.contentUrl.includes(EDashboardParams.general) ? EDashboardTypes.general : EDashboardTypes.team,
                    });
                }
            }
        });

        void app.initialize().then(() => {
            pages.config.registerOnSaveHandler((saveEvent) => {
                if (this.state.frameContext === FrameContexts.settings) {
                    if (this.state.selectedTab === EConfigTabs.panel) {
                        if (this.state.selectedPanelType == "equipe") {
                            void pages.config.setConfig({
                                entityId: "atendimento",
                                contentUrl: `${window.location.origin}/${EPanelRoutes.team}?appId=${this.appId}`,
                                suggestedDisplayName: "Atendimento",
                            });
                        } else if (this.state.selectedPanelType == "unidade") {
                            void pages.config.setConfig({
                                entityId: "atendimento",
                                contentUrl: `${window.location.origin}/${EPanelRoutes.unit}?appId=${this.appId}&ancora=${encodeURIComponent(this.state.ancora)}&title=${encodeURIComponent(this.state.meetingTitle)}`,
                                suggestedDisplayName: "Atendimento",
                            });
                        }
                    }

                    if (this.state.selectedTab === EConfigTabs.dashboard) {
                        if (this.state.selectedDashboardType == "geral") {
                            void pages.config.setConfig({
                                entityId: "dashboard",
                                contentUrl: `${window.location.origin}/${EDashboardRoute.route}?appId=${this.appId}&type=general`,
                                suggestedDisplayName: "Relatórios",
                            });
                        } else if (this.state.selectedDashboardType == "equipeDashboard") {
                            void pages.config.setConfig({
                                entityId: "dashboard",
                                contentUrl: `${window.location.origin}/${EDashboardRoute.route}?appId=${this.appId}&type=team`,
                                suggestedDisplayName: "Relatórios",
                            });
                        }
                    }
                    
                    saveEvent.notifySuccess();
                }

                if (this.state.frameContext === FrameContexts.sidePanel) {
                    pages.config.setConfig({
                        entityId: "Balcão Virtual",
                        contentUrl: `${window.location.origin}/in-meeting-panel`,
                        suggestedDisplayName: "Balcão Virtual",
                    });

                    saveEvent.notifySuccess();
                }
            });
        });
    }

    private init() {
        Promise.all([this.getMeetings()])
            .then(() => {
                this.setState({
                    loader: false
                });
            });
    }

    private getMeetings = async () => {
        const response = await getMeetingsData(null, Channels.bot);

        if (response.status === 200 && response.data) {
            const filteredMeetings = response.data;

            this.setState({
                meetings: this.makeDropdownItems(filteredMeetings, true),
                loading: false,
            }, () => {
                this.setState({
                    selectedMeeting: this.state.selectedAncora ? this.state.meetings.filter((g: any) => g.key == this.state.selectedAncora) : null,
                });
            });
        }
    }

    private onMeetingsChange = (event: any, items: any) => {
        if (items.value.length > 1) {
            this.setState({
                selectedMeeting: items.value[1],
                ancora: items.value[1].key,
                meetingTitle: items.value[1].header,
            });

            pages.config.setValidityState(true);
        }
        else if (items.value.length === 1) {
            this.setState({
                selectedMeeting: items.value[0],
                ancora: items.value[0].key,
                meetingTitle: items.value[0].header,
            });

            pages.config.setValidityState(true);
        }
        else {
            this.setState({
                selectedMeeting: null,
                ancora: null,
                meetingTitle: null,
            });

            pages.config.setValidityState(false);
        }
    }

    private setSelectedTab = (tab: EConfigTabs) => {
        this.setState({
            selectedTab: tab,
            selectedPanelType: null
        });
    }

    private makeDropdownItems = (items: any[] | undefined, customFields: boolean = false) => {
        const dropdownItem: dropdownItem[] = [];
        if (items) {
            items.forEach((element) => {
                dropdownItem.push({
                    key: customFields ? element.Ancora : element,
                    header: customFields ? element.name : element,
                });
            });
        }
        return dropdownItem;
    }

    public render(): JSX.Element {
        if (this.state.loader) {
            return (
                <div className="configContainer">
                    <Loader />
                </div>
            );
        }
        else {
            return (
                <Flex className="configContainer" gap="gap.large" column>
                    {this.state.selectedTab == null &&
                        <>
                            <Text content="Selecione o tipo de visualização que você deseja adicionar:" />

                            <Flex className="selectorContainer" gap="gap.medium">
                                <Flex className="tabSelector" onClick={() => this.setSelectedTab(EConfigTabs.panel)} gap="gap.medium" vAlign="center" column>
                                    <Flex gap="gap.medium" vAlign="center">
                                        <UserPhoneIcon className="icon" size="large" outline />
                                        <Text content="Atendimento" weight="bold" />
                                    </Flex>

                                    <Text content="Listagem de atendimentos do Balcão Virtual." size="small" />
                                </Flex>

                                <Flex className="tabSelector" onClick={() => this.setSelectedTab(EConfigTabs.dashboard)} gap="gap.medium" vAlign="center" column>
                                    <Flex gap="gap.medium" vAlign="center" >
                                        <PollIcon className="icon" size="large" outline />
                                        <Text content="Relatórios" weight="bold" />
                                    </Flex>

                                    <Text content="Índices e relatórios de atendimentos do Balcão Virtual." size="small" />
                                </Flex>
                            </Flex>
                        </>    
                    }
                    
                    {this.state.selectedTab === EConfigTabs.panel &&
                        <Flex column gap="gap.medium" fill>
                            <Text content="Qual tipo de painel de atendimento você deseja adicionar?" />
                            <RadioGroup
                                vertical
                                items={[
                                    {
                                        name: 'equipe',
                                        key: 'equipe',
                                        label: 'Unidades atendidas por esta equipe',
                                        value: 'equipe',
                                    },
                                    {
                                        name: 'unidade',
                                        key: 'unidade',
                                        label: 'Unidade específica',
                                        value: 'unidade',
                                    },
                                ]}
                                onCheckedValueChange={(e, props) => {
                                    this.setState({
                                        selectedPanelType: props.value,
                                        selectedMeeting: null,
                                    });

                                    pages.config.setValidityState(props.value == "equipe");
                                }}
                                defaultCheckedValue={this.state.selectedPanelType}
                            />

                            {this.state.selectedPanelType == "unidade" &&
                                <>
                                    <Text content="Selecione uma unidade de atendimento" />
                                    <Dropdown
                                        fluid
                                        search
                                        multiple
                                        loading={this.state.loading}
                                        items={this.state.meetings}
                                        value={this.state.selectedMeeting}
                                        onChange={this.onMeetingsChange}
                                        noResultsMessage={"Nenhuma ocorrência encontrada."}
                                    />
                                </>
                            }
                        </Flex>
                    }

                    {this.state.selectedTab === EConfigTabs.dashboard &&
                        <Flex column gap="gap.medium" fill>
                            <Text content="Qual tipo de relatório você deseja adicionar?" />
                            <RadioGroup
                                vertical
                                items={[
                                    {
                                        name: 'geral',
                                        key: 'geral',
                                        label: 'Relatório geral',
                                        value: 'geral',
                                    },
                                    {
                                        name: 'equipeDashboard',
                                        key: 'equipeDashboard',
                                        label: 'Relatório por equipe de atendimento',
                                        value: 'equipeDashboard',
                                    },
                                ]}
                                onCheckedValueChange={(e, props) => {
                                    this.setState({
                                        selectedDashboardType: props.value,
                                    });

                                    pages.config.setValidityState(true);
                                }}
                                defaultCheckedValue={this.state.selectedDashboardType}
                            />
                        </Flex>    
                    }
                    
                </Flex>
            );
        }
    }

}

const configAppWithTranslation = withTranslation()(ConfigTab);
export default configAppWithTranslation;
